import { NSARouterMakerResult, PaymentRequired } from "namirasoft-account-react";
import { GCPConstants, GCPFullRow, GCPInputRow, GCPRow } from "namirasoft-bill-watch";
import { NamingConvention, StringOperation } from "namirasoft-core";
import { NSCCostChart } from "namirasoft-cost-react";
import { GCPRow as CredentialGCPRow } from "namirasoft-credential";
import { LogGroupRow } from "namirasoft-log";
import { TopicRow } from "namirasoft-notification-sender";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxBoolean, NSBoxBooleans, NSBoxEntity, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import React, { createRef } from "react";
import { BillWatchEntities } from "../BillWatchEntities";
import { BillWatchLayout } from '../BillWatchLayout';
import { BillWatchProps } from "../BillWatchProps";
import LookbackPeriods from "../components/LookbackPeriods";
import Rules from "../components/Rules";

interface GCPEditPageProps extends NSARouterMakerResult, BillWatchProps
{
    isEdit: boolean;
}

export default class GCPEditPage extends React.Component<GCPEditPageProps>
{
    NSBoxEntity_CredentialID = createRef<NSBoxEntity<CredentialGCPRow>>();
    NSBoxString_AccountID = createRef<NSBoxString>();
    NSBoxEntity_TopicID = createRef<NSBoxEntity<TopicRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxBoolean_Active = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowAPICall = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowNewCost = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowOnDeactive = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowOnNoAnomaly = createRef<NSBoxBoolean>();
    NSBoxInteger_MaxAPICallTotal = createRef<NSBoxInteger>();
    NSBoxInteger_MaxAPICallPerOperation = createRef<NSBoxInteger>();
    NSBoxString_ProjectID = createRef<NSBoxString>();
    NSBoxBooleans_Dimensions = createRef<NSBoxBooleans>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();
    Rules_Main = createRef<Rules>();
    LookbackPeriods_Main = createRef<LookbackPeriods>();

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <PaymentRequired
                {...this.props}
                tilte="Subscription is Required to Start Watching"
                description="Want to start watching your GCP usage? Subscribe to activate Bill Watch and begin tracking your costs with confidence. New users enjoy a 2-week free trial with no upfront fees. Subscribers may cancel their subscription at any time with no extra cost."
                button={{ action: "Activate" }}
                payment_offer_url="https://payment.namirasoft.com/offer/list/cost/usage"
                Validation={this.props.servers.bill_watch.gcp.Validation}
            >
                <BillWatchLayout<GCPRow, GCPInputRow>
                    key="main"
                    {...this.props}
                    action={{
                        description: this.props.isEdit ? "You can edit a previous GCP watch and change its settings." : "You can create a new watch for GCP to monitor costs. Enter the required information, including account credentials and watch parameters.",
                    }}
                    pages={{
                        entity: BillWatchEntities.gcp(this.props, () => { }),
                        edit: {
                            ...this.props,
                            ui: {
                                tab_page_edit: {
                                    editor: {
                                        getInputRow: (): GCPInputRow =>
                                        {
                                            let credential_id: string = this.NSBoxEntity_CredentialID.current?.getValueOne() ?? "";
                                            let account_id: string = this.NSBoxString_AccountID.current?.getValue() ?? "";
                                            let topic_id: string = this.NSBoxEntity_TopicID.current?.getValueOne() ?? "";
                                            let log_group_id: string = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? "";
                                            let name: string = this.NSBoxString_Name.current?.getValue() ?? "";
                                            let active: boolean = this.NSBoxBoolean_Active.current?.getValue() ?? false;
                                            let show_api_call: boolean = this.NSBoxBoolean_ShowAPICall.current?.getValue() ?? false;
                                            let show_new_cost: boolean = this.NSBoxBoolean_ShowNewCost.current?.getValue() ?? false;
                                            let show_on_deactive: boolean = this.NSBoxBoolean_ShowOnDeactive.current?.getValue() ?? false;
                                            let show_on_no_anomaly: boolean = this.NSBoxBoolean_ShowOnNoAnomaly.current?.getValue() ?? false;
                                            let max_api_call_total: number = this.NSBoxInteger_MaxAPICallTotal.current?.getValue() ?? 0;
                                            let max_api_call_per_operation: number = this.NSBoxInteger_MaxAPICallPerOperation.current?.getValue() ?? 0;
                                            let rule_ids = this.Rules_Main.current?.NSRepeater_Rules.current?.getValue() ?? [];
                                            let lookback_periods = this.LookbackPeriods_Main.current?.NSRepeater_LookbackPeriods.current?.getValue() ?? [];
                                            let project_id = this.NSBoxString_ProjectID.current?.getValue() ?? "";
                                            let dimensions = this.NSBoxBooleans_Dimensions.current?.getValues() ?? [];
                                            let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                            return {
                                                workspace_id: "",
                                                credential_id,
                                                account_id,
                                                topic_id,
                                                log_group_id,
                                                name,
                                                active,
                                                show_api_call,
                                                show_new_cost,
                                                show_on_deactive,
                                                show_on_no_anomaly,
                                                max_api_call_total,
                                                max_api_call_per_operation,
                                                rule_ids,
                                                lookback_periods,
                                                project_id,
                                                dimensions,
                                                description,
                                                gcp_category: [],
                                                gcp_field: [],
                                                gcp_tag: [],
                                            };
                                        },
                                        setRow: (row) =>
                                        {
                                            let full_row = row as GCPFullRow;
                                            this.NSBoxEntity_CredentialID.current?.setValue(row.credential_id);
                                            this.NSBoxString_AccountID.current?.setValue(row.account_id);
                                            this.NSBoxEntity_TopicID.current?.setValue(row.topic_id);
                                            this.NSBoxString_ProjectID.current?.setValue(row.project_id);
                                            this.NSBoxEntity_LogGroupID.current?.setValue(row.log_group_id);
                                            this.NSBoxString_Name.current?.setValue(row.name);
                                            this.NSBoxBoolean_Active.current?.setValue(row.active);
                                            this.NSBoxBoolean_ShowAPICall.current?.setValue(row.show_api_call);
                                            this.NSBoxBoolean_ShowNewCost.current?.setValue(row.show_new_cost);
                                            this.NSBoxBoolean_ShowOnDeactive.current?.setValue(row.show_on_deactive);
                                            this.NSBoxBoolean_ShowOnNoAnomaly.current?.setValue(row.show_on_no_anomaly);
                                            this.NSBoxInteger_MaxAPICallTotal.current?.setValue(row.max_api_call_total);
                                            this.NSBoxInteger_MaxAPICallPerOperation.current?.setValue(row.max_api_call_per_operation);
                                            this.Rules_Main.current?.NSRepeater_Rules.current?.setValue(full_row.gcp_rule.map(r => r.rule_id));
                                            this.LookbackPeriods_Main.current?.NSRepeater_LookbackPeriods.current?.setValue(full_row.lookback_periods);
                                            this.NSBoxBooleans_Dimensions.current?.setValues(StringOperation.split(row.dimensions));
                                            this.NSBoxTextArea_Description.current?.setValue(row.description);
                                        },
                                    },
                                    ui: {
                                        extra_tabs: [
                                            {
                                                title: "Rules",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-rules"
                                                },
                                                getContent: () =>
                                                {
                                                    return <Rules
                                                        ref={this.Rules_Main}
                                                        {...this.props}
                                                        name="GCPEditPage"
                                                    />;
                                                }
                                            },
                                            {
                                                title: "Lookback Periods",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-lookback-periods"
                                                },
                                                getContent: () =>
                                                {
                                                    return <LookbackPeriods
                                                        ref={this.LookbackPeriods_Main}
                                                        {...this.props}
                                                        name="GCPEditPage"
                                                    />;
                                                }
                                            },
                                            {
                                                title: "Dimensions",
                                                clicked: true,
                                                getContent: () => (
                                                    <NSRow style={{ maxWidth: NSBox.width.triple }}>
                                                        <NSBoxBooleans
                                                            ref={this.NSBoxBooleans_Dimensions}
                                                            box_all={{ hideHeader: true }}
                                                            boxes={{
                                                                hideHeader: true,
                                                                defaultValue: ["TOTAL", ...GCPConstants.DIMENSIONS]
                                                            }}
                                                            items={GCPConstants.getDimensionCombinations().map(r =>
                                                            {
                                                                let keys = r.keys;
                                                                if (keys.length === 0)
                                                                    keys = ["Total"];
                                                                return {
                                                                    value: r.name,
                                                                    text: NamingConvention.auto.convert("By " + keys.join(" and "), NamingConvention.Pascal_Case_Space)
                                                                };
                                                            })}
                                                        />
                                                    </NSRow>
                                                )
                                            },
                                        ]
                                    }
                                },
                                tab_page_view: {
                                    tabs: {
                                        extra: {
                                            after_history: [
                                                NSCCostChart.getTab(this.props)
                                            ]
                                        }
                                    }
                                }
                            }
                        }
                    }}
                >
                    <NSColumn>
                        <NSRow>
                            <NSBoxString
                                ref={this.NSBoxString_Name}
                                required
                                title="Name"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-name" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_Active}
                                required
                                defaultValue={true}
                                title="Active"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-active" }}
                            />
                        </NSRow>
                        <NSBoxEntity<CredentialGCPRow>
                            ref={this.NSBoxEntity_CredentialID}
                            required
                            title="GCP Credential"
                            multiple={false}
                            table={namirasoft.credential.tables.gcp}
                            getTitle={item => item.name}
                            getValue={item => item.id}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-credential-id" }}
                            style={{ width: NSBox.width.double }}
                        />
                        <NSBoxString
                            ref={this.NSBoxString_AccountID}
                            required
                            title="GCP Account ID"
                            info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-gcp-account-id" }}
                            style={{ width: NSBox.width.double }}
                        />
                        <NSBoxString
                            ref={this.NSBoxString_ProjectID}
                            required
                            title="Project ID"
                            info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-project-id" }}
                            style={{ width: NSBox.width.double }}
                        />
                        <NSRow>
                            <NSBoxEntity<TopicRow>
                                ref={this.NSBoxEntity_TopicID}
                                required
                                title="Topic"
                                multiple={false}
                                table={namirasoft.notification_sender.tables.topic}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() => null}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-topic-id" }}
                            />
                            <NSBoxEntity<LogGroupRow>
                                ref={this.NSBoxEntity_LogGroupID}
                                required={true}
                                title="Log Group"
                                multiple={false}
                                table={namirasoft.log.tables.log_group}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() => null}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-log-group-id" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowAPICall}
                                title="Show API Call"
                                required
                                hideHeader={true}
                                defaultValue={true}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-show-api-call" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowNewCost}
                                title="Show New Cost"
                                required
                                hideHeader={true}
                                defaultValue={true}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-show-new-cost" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowOnDeactive}
                                title="Show On Deactive"
                                required
                                hideHeader={true}
                                defaultValue={true}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-show-on-deactive" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowOnNoAnomaly}
                                title="Show On No Anomaly"
                                required
                                hideHeader={true}
                                defaultValue={true}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-show-on-no-anomaly" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxInteger
                                ref={this.NSBoxInteger_MaxAPICallTotal}
                                required
                                defaultValue={100}
                                title="Max API Call Total"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-max-api-call-total" }}
                            />
                            <NSBoxInteger
                                ref={this.NSBoxInteger_MaxAPICallPerOperation}
                                required
                                defaultValue={5}
                                title="Max API Call Per Operation"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-max-api-call-per-operation" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxTextArea
                                ref={this.NSBoxTextArea_Description}
                                required={false}
                                title="Description"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/gcp/#field-description" }}
                            />
                        </NSRow>
                    </NSColumn>
                </BillWatchLayout>
            </PaymentRequired>
        );
    }
}